/**
 * Russian strings for the October 2026 redesign (decision 9.1, X.2).
 *
 * Only the keys this redesign added are here. The rest of the app is not translated, and a
 * key missing from this dictionary falls back to `uz` (see `index.ts`). A person chooses Russian
 * in their own profile (D9.110); nobody is switched to it by the agency.
 */
import { ru09Clients } from "./ru-09-clients";
import { ru09Home } from "./ru-09-home";
import { ruInbox09 } from "./ru-09-inbox";
import { ru09Tours } from "./ru-09-tours";
import type { TranslationKey } from "./uz";

export const ru: Partial<Record<TranslationKey, string>> = {
  ...ru09Clients,
  ...ru09Home,
  ...ruInbox09,
  ...ru09Tours,
  "nav.murojaatlar": "Обращения",
  "nav.bronlar": "Брони",
  "nav.aiAssistant": "AI-помощник",
  "nav.team": "Команда и филиалы",
  "nav.distribution": "Правила распределения",
  "nav.badgeCount": "{count} шт.",

  "settings.nav.agency": "Данные агентства",
  "settings.nav.team": "Команда и филиалы",
  "settings.nav.distribution": "Правила распределения",
  "settings.nav.pricing": "Цены и наценки",
  "settings.nav.operators": "Подключение туроператоров",
  "settings.nav.catalog": "Настройка каталога",
  "settings.nav.documents": "Требования к документам",
  "settings.placeholder": "Этот раздел скоро будет готов.",
  "settings.distribution.rule": "Новые обращения получают только операторы в статусе «На работе». Сотруднику на перерыве или со статусом «Не на работе» новые диалоги не назначаются.",
  "settings.layout.label": "Разделы настроек",

  "pricing.title": "Цены и наценки",
  "pricing.minMargin": "Минимальная маржа, %",
  "pricing.minMarginHelp": "Скидки ниже этого уровня уходят на подтверждение владельцу.",
  "pricing.managersApprove": "Руководитель филиала тоже может подтверждать скидку",
  "pricing.operatorsSeeMargin": "Операторы видят нетто и маржу",
  "pricing.operatorsSeeMarginOn": "Включено: оператор видит себестоимость и маржу в сделке.",
  "pricing.operatorsSeeMarginOff": "Выключено: оператор видит только брутто-цену.",
  "pricing.markupTitle": "Наценка по типам услуг",
  "pricing.markupHelp": "Если правило для тура не найдено, добавляется этот процент.",
  "pricing.markupTour": "Тур, %",
  "pricing.markupHotel": "Только отель, %",
  "pricing.markupPackage": "Свой пакет, %",
  "pricing.save": "Сохранить",
  "pricing.saved": "Сохранено",

  "approvals.queue.title": "Нужно ваше решение",
  "approvals.queue.empty": "Сейчас нет запросов, ожидающих подтверждения",
  "approvals.queue.error": "Не удалось загрузить запросы",
  "approvals.discount.title": "Запрос на скидку",
  "approvals.discount.change": "{from} → {to}",
  "approvals.margin.badge": "Маржа {margin}% — минимум {min}%",
  "approvals.reassign.title": "Передача обращения",
  "approvals.requestedBy": "запросил: {name}",
  "approvals.approve": "Подтвердить",
  "approvals.reject": "Отклонить",
  "approvals.rejectReason": "Причина отказа",
  "approvals.rejectConfirm": "Подтвердить отказ",
  "approvals.cancel": "Отмена",
  "approvals.decided": "Решение принято",
  "auth.twoFactor.title": "Подтвердите в Telegram",
  "auth.twoFactor.body": "Мы отправили сообщение в Telegram-бот. Нажмите «Подтвердить» — страница откроется сама.",
  "auth.twoFactor.cancel": "Отмена",
  "auth.twoFactor.denied": "Вход отклонён в Telegram. Смените пароль.",
  "auth.twoFactor.expired": "Время подтверждения истекло. Войдите снова.",
  "status.working": "На работе",
  "status.break": "Перерыв",
  "status.off": "Не на работе",
  "status.note": "В статусе «На работе» новые обращения назначаются вам.",
  "status.label": "Статус",
  "shell.menu.week": "На этой неделе: {count} продаж · Мой отчёт →",
  "shell.menu.notifications": "Уведомления",
  "shell.menu.theme": "Тема",
  "shell.menu.language": "Язык",
  "shell.menu.shortcuts": "Сочетания клавиш",
  "shell.menu.help": "Помощь и руководство",
  "shell.menu.agencySettings": "Настройки агентства",
  "shell.menu.profileSettings": "Настройки профиля",
  "shell.shortcuts.title": "Сочетания клавиш",
  "shell.shortcuts.palette": "Поиск и переход",
  "shell.shortcuts.help": "Этот список",
  "shell.help.body": "Руководство готовится. С вопросами обращайтесь к владельцу агентства или в поддержку Kapitan.",
  "theme.light": "Светлая",
  "theme.dark": "Тёмная",
  "theme.system": "Как на устройстве",
  "language.uz": "Узбекский",
  "language.ru": "Русский",
  "profile.title": "Профиль",
  "profile.sections": "Разделы профиля",
  "profile.section.personal": "Личные данные",
  "profile.section.schedule": "График работы",
  "profile.section.appearance": "Внешний вид",
  "profile.section.notifications": "Уведомления",
  "profile.section.signature": "Подпись",
  "profile.section.security": "Безопасность",
  "profile.fullName": "Имя и фамилия",
  "profile.displayName": "Имя для клиентов",
  "profile.phone": "Телефон",
  "profile.email": "Email",
  "profile.telegram": "Telegram",
  "profile.telegramNone": "Не подключён",
  "profile.role": "Роль",
  "profile.branch": "Филиал",
  "profile.hireDate": "Дата найма",
  "profile.ownerOnly": "Меняет только владелец",
  "profile.photo": "Фото",
  "profile.photoUpload": "Загрузить фото",
  "profile.photoRemove": "Удалить фото",
  "profile.saved": "Сохранено",
  "profile.schedule.days": "Рабочие дни",
  "profile.schedule.from": "Начало",
  "profile.schedule.to": "Окончание",
  "profile.schedule.autoOff": "Автоматически «Не на работе» после рабочего времени",
  "profile.schedule.day0": "Пн",
  "profile.schedule.day1": "Вт",
  "profile.schedule.day2": "Ср",
  "profile.schedule.day3": "Чт",
  "profile.schedule.day4": "Пт",
  "profile.schedule.day5": "Сб",
  "profile.schedule.day6": "Вс",
  "profile.leave.title": "Отпуск и больничный",
  "profile.leave.hint": "Вступает в силу после подтверждения менеджером.",
  "profile.leave.kind": "Тип",
  "profile.leave.kindLeave": "Отпуск",
  "profile.leave.kindSick": "Больничный",
  "profile.leave.from": "Дата начала",
  "profile.leave.to": "Дата окончания",
  "profile.leave.handover": "Кто примет клиентов",
  "profile.leave.note": "Комментарий",
  "profile.leave.submit": "Отправить запрос",
  "profile.leave.cancel": "Отменить",
  "profile.leave.empty": "Запросов нет",
  "profile.leave.status.pending": "Ожидает",
  "profile.leave.status.approved": "Подтверждено",
  "profile.leave.status.rejected": "Отклонено",
  "profile.leave.status.cancelled": "Отменено",
  "profile.appearance.theme": "Тема",
  "profile.appearance.language": "Язык интерфейса",
  "profile.appearance.currency": "Отображение валюты",
  "profile.appearance.currencySymbol": "Символ ($, сум)",
  "profile.appearance.currencyCode": "Код (USD, UZS)",
  "profile.quiet.title": "Тихий режим",
  "profile.quiet.hint": "В это время обновляется только колокольчик в системе. Важные уведомления приходят всегда.",
  "profile.quiet.enabled": "Включить тихий режим",
  "profile.quiet.from": "С",
  "profile.quiet.to": "До",
  "profile.connectBot": "Подключить Telegram-бот →",
  "profile.signature.label": "Подпись сообщений",
  "profile.signature.hint": "Добавляется в конец ваших сообщений. Добавляется и к текстам, подготовленным AI.",
  "profile.signature.preview": "Как будет выглядеть",
  "profile.security.sessions": "Активные сеансы",
  "profile.security.thisDevice": "Это устройство",
  "profile.security.lastSeen": "Последняя активность: {when}",
  "profile.security.revoke": "Завершить",
  "profile.security.revokeOthers": "Выйти на всех других устройствах",
  "profile.security.revoked": "Завершено сеансов: {count}",
  "profile.security.twoFactor": "Двухэтапный вход (Telegram)",
  "profile.security.twoFactorHint": "После пароля вы подтверждаете вход кнопкой в Telegram-боте.",
  "profile.security.twoFactorNeedsTelegram": "Сначала подключите Telegram.",
  "profile.security.unknownDevice": "Неизвестное устройство",
  "team.title": "Состояние команды",
  "team.empty": "Сотрудников нет",
  "team.col.name": "Сотрудник",
  "team.col.status": "Статус",
  "team.col.schedule": "График",
  "team.col.leave": "Отпуск",
  "team.noSchedule": "Без графика",
  "team.leaveBadge": "В отпуске · до {until} · клиенты у {name}",
  "team.leaveUpcoming": "Отпуск: {from} – {until}",
  "ai.tabs.label": "Разделы AI-помощника",
  "ai.tabs.agent": "AI-агент",
  "ai.tabs.automations": "Автоматизация",
  "ai.tabs.campaigns": "Кампании",
  "shell.menu.profile": "Профиль",
  "shell.menu.switchAgency": "Выбрать агентство",
  "shell.menu.platform": "Панель платформы",
  "shell.menu.userLabel": "Меню аккаунта",
  "profile.notifications.title": "Уведомления",
  "profile.notifications.hint":
    "Выберите, по какому каналу получать каждое уведомление. Важные уведомления отключить нельзя.",
  "profile.notifications.locked": "Важное: всегда включено",
  "profile.notifications.soon": "Скоро",
  "profile.notifications.saveFailed": "Не удалось сохранить. Попробуйте ещё раз.",
  "profile.notifications.empty": "Список уведомлений пуст",
};
